import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { doc, updateDoc } from "firebase/firestore";
import { setGame } from "stores/game/GameSlice";
import {
  turns,
  soloTurns,
  fetchChampions,
  validatedPickBanArrayByTurn,
} from "constants/gameDefaults";
import { motion } from "framer-motion";
import RoleFilter from "components/RoleFilter";
import { GameModeEnum, GameStateEnum } from "interfaces/game";
import { Tooltip } from "react-tooltip";
import useSound from "use-sound";

import banSound from "styles/audio/ban.ogg";
import pickSound from "styles/audio/pick.ogg";
import notifBanSound from "styles/audio/notifban.ogg";
import notifPickSound from "styles/audio/notifpick.ogg";

function Draft({ db, canDoAnAction }) {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.userReducer.user);
  const game = useSelector((state) => state.gameReducer.game, shallowEqual);

  const [actionsTaken, setActionsTaken] = useState(0);
  const [actionInProgress, setActionInProgress] = useState(false);
  // If the game state has not been fully initialized yet, assume this is turn 0.
  const turn = game?.currentTurn || 0;
  const turnsToUse = game?.mode === GameModeEnum.SIMULATION ? soloTurns : turns;
  // TODO - revamp array logic which is currently a bit error prone.
  const currentSide = turnsToUse[turn]?.side;
  const currentPlayer = game?.players[currentSide];
  const turnType = turnsToUse[turn]?.type;
  const actionNb = turnsToUse[turn]?.nbAction;
  const isPlayerTurn = currentPlayer === user?.uid;

  const initialChampionsList = fetchChampions(game?.mode);
  const [filteredChampionsList, setFilteredChampionsList] = useState(new Map());

  const isMuted = useSelector((state) => state.audioReducer.isMuted);
  const [playBanSound] = useSound(banSound);
  const [playPickSound] = useSound(pickSound);
  const [playNotifBanSound] = useSound(notifBanSound, { volume: 0.5 });
  const [playNotifPickSound] = useSound(notifPickSound, { volume: 0.5 });

  useEffect(() => {
    const fetchAndSetChampions = async () => {
      setFilteredChampionsList(initialChampionsList);
    };

    fetchAndSetChampions();
  }, []);

  useEffect(() => {
    if (isPlayerTurn && !isMuted) {
      if (turnType === "ban") {
        playNotifBanSound();
      } else if (turnType === "pick") {
        playNotifPickSound();
      }
    }
  }, [turn]);

  const handleAction = async (championId) => {
    const side = currentSide === 0 ? "blue" : "red";
    const arrayName =
      turnType === "ban" ? `${side}BannedChampions` : `${side}PickedChampions`;

    if (
      isPlayerTurn &&
      actionInProgress === false &&
      canDoAnAction &&
      (turnType === "ban" || turnType === "pick")
    ) {
      setActionInProgress(true);
      const updatedChampions = validatedPickBanArrayByTurn({
        pickBanArray: [
          ...(game?.[arrayName] || []),
          { championId, userId: user.uid },
        ],
        currentTurn: turn,
        side: currentSide,
      });
      const newActionsTaken = actionsTaken + 1;
      const isLastActionOfTurn = newActionsTaken === actionNb;

      if (isLastActionOfTurn) {
        // Single atomic write: champions + turn advance (+ state change if draft is over)
        const nextTurn = (game?.currentTurn ?? 0) + 1;
        const updatePayload = {
          [arrayName]: updatedChampions,
          currentTurn: nextTurn,
        };
        if (nextTurn === Object.keys(turns).length) {
          updatePayload.state = GameStateEnum.ROLE_SELECTION;
        }
        await updateDoc(doc(db, "games", game?.id), updatePayload);
        setActionsTaken(0);
      } else {
        // Mid-turn action (e.g. first of a double pick): only update champions
        await updateDoc(doc(db, "games", game?.id), {
          [arrayName]: updatedChampions,
        });
        setActionsTaken(newActionsTaken);
      }
    } else if (
      actionInProgress === false &&
      game.mode === GameModeEnum.SIMULATION
    ) {
      setActionInProgress(true);
      const updatedChampions = [
        ...(game?.[arrayName] || []),
        {
          championId,
          userId: currentSide === 0 ? "player1" : "player2",
        },
      ].slice(0, 5); // limit to 5 picks max

      const updatedGame = {
        ...game,
        [currentSide === 0 ? "bluePickedChampions" : "redPickedChampions"]:
          updatedChampions,
      };

      dispatch(setGame(updatedGame));

      const newActionsTaken = actionsTaken + 1;
      setActionsTaken(newActionsTaken);
      if (newActionsTaken === actionNb) {
        const nextTurn = (game.currentTurn ?? 0) + 1;
        dispatch(setGame({ ...updatedGame, currentTurn: nextTurn }));
        if (
          nextTurn ===
          Object.keys(game.mode === GameModeEnum.SIMULATION ? soloTurns : turns)
            .length
        ) {
          dispatch(
            setGame({
              ...updatedGame,
              state: GameStateEnum.ROLE_SELECTION,
            }),
          );
        }

        setActionsTaken(0);
      }
    }

    if ((isPlayerTurn || game?.mode === GameModeEnum.SIMULATION) && !isMuted) {
      if (turnType === "ban") {
        playBanSound();
      } else if (turnType === "pick" && !isMuted) {
        playPickSound();
      }
    }

    setActionInProgress(false);
  };

  const renderChampionButton = (champion, index) => {
    const isPickOrBan =
      game.bluePickedChampions.some(
        (pick) => pick.championId === champion.id,
      ) ||
      game.redPickedChampions.some((pick) => pick.championId === champion.id) ||
      game.blueBannedChampions.some(
        (pick) => pick.championId === champion.id,
      ) ||
      game.redBannedChampions.some((pick) => pick.championId === champion.id);

    const championInList = initialChampionsList.get(champion.id);
    if (!championInList) {
      return null;
    }

    if (turnType !== "ban") {
      if (champion.id === "NoBan") {
        return null;
      }
    }

    return (
      <React.Fragment key={`champion${index}`}>
        <motion.div
          className="championPickerCard left"
          id={`champion${index}`}
          key={`champion${index}`}
          data-tooltip-id={`champion${index}`}
          data-tooltip-content={champion.name}
          onClick={() => {
            if (!isPickOrBan || champion.id === "NoBan") {
              handleAction(champion.id);
            }
          }}
        >
          <img
            src={champion.icon}
            draggable={false}
            alt={`${champion.name}Icon`}
            style={{
              objectFit: "cover", // do not shrink with height
              filter:
                isPickOrBan && champion.id !== "NoBan"
                  ? "grayscale(100%)"
                  : "none",
            }}
          />
        </motion.div>
        <Tooltip id={`champion${index}`} />
      </React.Fragment>
    );
  };

  return (
    <section className="Draft championPickerContainer">
      <RoleFilter
        initialChampionsList={initialChampionsList}
        setFilteredChampionsList={setFilteredChampionsList}
      />
      <div className="championPickerWrapper">
        {[...filteredChampionsList.values()].map((champion, index) =>
          renderChampionButton(champion, index),
        )}
      </div>
    </section>
  );
}

export default Draft;
